import { useEffect, useState } from 'react'
import { useOutlet, useMatches, useLocation } from 'react-router-dom'
import { placed } from '@/lib/style-guard'
import { cn } from '@/lib/utils'

/** The outlet's frame is the primitive's (none by default, for layouts that frame the page themselves): `page` is the one scroll area and gutter (`page-scroller`
 *  in index.css), `fill` a full-height column for a page that scrolls inside itself (messages). */
const FRAME = { page: 'page-scroller', fill: 'flex min-h-0 flex-1 min-w-0 flex-col' } as const

interface Props {
  frame?: keyof typeof FRAME
  /** Placement only (the style guard refuses a look). */
  className?: string
}

/**
 * Drop-in replacement for <Outlet />, keyed by route so a genuine page change remounts. It used to
 * crossfade; see the note in the body for why it no longer does.
 *
 * Design choices (chosen to feel premium, not janky):
 *
 * - **Keyed by route ID, not pathname.** `useMatches()` gives us the leaf
 *   route's auto-generated ID, which is stable across navigations with the
 *   same route pattern but different params. So `/messages/chatA` and
 *   `/messages/chatB` share a key and stay in place (no page crossfade);
 *   only the internal conversation swaps. Same for `/event/:id` with
 *   different ids. This was the root cause of the chat-switch flicker —
 *   keying by pathname remounted the page on every param change, flashing
 *   through the "no conversation selected" empty state.
 *
 */
export function AnimatedOutlet({ frame, className }: Props) {
  const matches = useMatches()
  // Freeze each route element into its keyed transition. A live <Outlet /> in
  // the exiting wrapper would resolve the NEW route too, mounting it twice.
  const outlet = useOutlet()

  // Leaf route's auto-generated ID. Same across param changes on the same
  // route pattern (e.g. /messages/:eventId with different IDs).
  const routeKey = matches[matches.length - 1]?.id ?? 'root'
  const { pathname } = useLocation()

  // No crossfade (sweep, 24.10.2026): the leaving page faded out OVER the arriving page's
  // skeleton, and the arriving cards then entered again, two motions for one step. The page's own
  // pieces carry the entrance (`enter`, `[data-stagger]`); the outlet only swaps.
  return <RouteFrame key={routeKey} className={cn(frame && FRAME[frame], placed(className, 'AnimatedOutlet'))} visitKey={`${routeKey}:${pathname}`}>{outlet}</RouteFrame>
}

/** Pages already opened in this tab. A page's entrance plays once; coming back, it is simply there. */
const visited = new Set<string>()

/**
 * Entrances (`enter`, `grow-*`, count-up) play the FIRST time a page opens in a session, and not on
 * every return (sweep 24.10.2026: going back to Oversikt replayed 97 meter segments and every
 * count-up). On a return the frame carries `data-motion="settled"` for its first moments, which
 * index.css and `useCountUp` read; anything the reader triggers after that (a day switch, a panel)
 * still moves.
 */
function RouteFrame({ visitKey, className, children }: { visitKey: string; className?: string; children: React.ReactNode }) {
  const [settled, setSettled] = useState(() => visited.has(visitKey))
  useEffect(() => {
    visited.add(visitKey)
    if (!settled) return
    const id = window.setTimeout(() => setSettled(false), 900)
    return () => window.clearTimeout(id)
  }, [visitKey, settled])
  return <div className={className} data-motion={settled ? 'settled' : undefined}>{children}</div>
}
